import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity } from './GranularitySelector';
import { AuthenticationFilters } from './AuthenticationFilters';
import { EmptyState } from './EmptyState';
interface DateRange {
  start: Date;
  end: Date;
}
interface AuthenticationTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function AuthenticationTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AuthenticationTabProps) {
  const {
    t
  } = useTranslation("analytics");
  // Filter states - no successRateMetric for Authentication tab
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDeduplication('deduplicated');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setInteractionTypes([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-1470">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-1471">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-1472" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-1473" />
        
        <AuthenticationFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-1474" />
        
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1475">
        <div className="p-6 border-b border-gray-200" data-id="element-1476">
          <div className="flex items-center justify-between" data-id="element-1477">
            <div className="flex items-center gap-4" data-id="element-1478">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1479">
                {t("components.AcceptanceTab.attrs.key_metrics")}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1480">
                <span data-id="element-1481">{t("components.AuthenticationTab.compared_to")}</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1482">
                  {t("components.AuthenticationTab.previous_period")}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" data-id="element-1483">
                    
                    <path d="M4.427 9.573l3.396-3.396a.25.25 0 01.354 0l3.396 3.396a.25.25 0 01-.177.427H4.604a.25.25 0 01-.177-.427z" data-id="element-1484" />
                  </svg>
                </button>
                <span className="text-gray-500" data-id="element-1485">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-1486">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1488">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1487" />
          
        </div>
      </div>

      {/* 3D Secure authentication outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1489">
        <div className="p-6 border-b border-gray-200" data-id="element-1490">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1491">
            {t("components.AuthenticationTab.3d_secure_authentication_outcomes")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1492">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1494">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1493" />
          
        </div>
      </div>

      {/* 3D Secure authentication request reasons */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1495">
        <div className="p-6 border-b border-gray-200" data-id="element-1496">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1497">
            {t("components.AuthenticationTab.3d_secure_authentication_request_reasons")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1498">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1500">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1499" />
          
        </div>
      </div>
    </div>;
}